import React from "react";
import { Form, Button } from "react-bootstrap";
import TextInput from "../components/TextInput";
import CommonHeader from "../containers/CommonHeader";
import { PhoneInput } from "../components/NumberInput";
import useGoTo from "../hooks/useGoTo";
import useFinishLogin from "../hooks/useFinishLogin";
import { validatePhoneNumber } from "../utils/validate";
import { useDispatch, useSelector } from "react-redux";
import useToast from "../hooks/useToast";
import {
  apiError,
  errorMessage,
  sendResetCode,
  signIn,
  userStatus,
} from "../services/fidelity";
import { setUserPhone, setUserId } from "../ducks/userSlice";

const STEP_PHONE = "PHONE";
const STEP_PASSWORD = "PASSWORD";

export default function LoginScreen() {
  const dispatch = useDispatch();
  const toast = useToast();
  const goTo = useGoTo();
  const finishLogin = useFinishLogin();
  const { userPhone } = useSelector((state) => state.user);
  const [step, setStep] = React.useState(STEP_PHONE);
  const [password, setPassword] = React.useState("");
  const [passwordError, setPasswordError] = React.useState("");
  const [isLoading, setLoading] = React.useState(false);

  const onChangePhone = (event) => {
    dispatch(setUserPhone({ phone: event.target.value }));
    // trocou o número: o que o status disse não vale mais
    setStep(STEP_PHONE);
    setPassword("");
    setPasswordError("");
  };

  const onChangePassword = (event) => {
    setPassword(event.target.value);
    setPasswordError("");
  };

  // Cliente antigo sem senha e "esqueci minha senha" caem os dois aqui.
  const askCode = async () => {
    const response = await sendResetCode(userPhone).then((resp) => resp.json());
    if (response.error) {
      throw apiError(response, "Não foi possível enviar o código.");
    }

    dispatch(setUserId({ id: response.id, sendCodeChannel: response.channel }));
    goTo("/confirmation");
  };

  const checkPhone = async () => {
    const response = await userStatus(userPhone).then((resp) => resp.json());
    if (response.error) {
      throw apiError(response, "Não foi possível continuar.");
    }

    if (response.status === "NOVO") {
      goTo("/register");
      return;
    }

    if (response.status === "SEM_SENHA") {
      await askCode();
      return;
    }

    setStep(STEP_PASSWORD);
  };

  const enter = async () => {
    const response = await signIn(userPhone, password).then((resp) =>
      resp.json(),
    );
    if (response.error) {
      const error = apiError(response, "Não foi possível entrar.");
      if (error.statusCode === 401) {
        setPasswordError("Senha incorreta.");
        return;
      }

      // conta sem senha: manda criar uma em vez de insistir no login
      if (error.code === "TELEFONE_SEM_SENHA") {
        await askCode();
        return;
      }

      throw error;
    }

    finishLogin(response);
  };

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await (step === STEP_PHONE ? checkPhone() : enter());
    } catch (error) {
      toast.toastError(
        errorMessage(error, "Erro de conexão. Por favor, tente novamente."),
      );
    } finally {
      setLoading(false);
    }
  };

  // Button do react-bootstrap não define type: dentro do Form ele vira submit
  // e dispararia o login junto.
  const forgotPassword = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await askCode();
    } catch (error) {
      toast.toastError(
        errorMessage(error, "Erro de conexão. Por favor, tente novamente."),
      );
    } finally {
      setLoading(false);
    }
  };

  const isPasswordStep = step === STEP_PASSWORD;
  const phoneError = validatePhoneNumber(userPhone) ? "" : "Celular inválido";
  const isDisabled =
    isLoading ||
    !!phoneError ||
    (isPasswordStep && password.trim().length === 0);

  return (
    <>
      <CommonHeader text="Entre ou cadastre-se" />
      <div className="mt-3 text-center">
        <strong>
          {isPasswordStep ? "Informe sua senha" : "Informe seu celular com DDD"}
        </strong>
      </div>
      <Form className="mt-4 px-3 px-lg-0" onSubmit={submit}>
        <TextInput
          className="mt-3"
          autoFocus={!isPasswordStep}
          label=""
          value={userPhone}
          onChange={onChangePhone}
          error={!!phoneError}
          helperText={phoneError}
          inputComponent={PhoneInput}
          inputProps={{ autoComplete: "username" }}
          required
          disabled={isLoading}
        />
        {isPasswordStep && (
          <TextInput
            className="mt-3"
            autoFocus
            type="password"
            label="Senha"
            value={password}
            onChange={onChangePassword}
            error={!!passwordError}
            helperText={passwordError}
            inputProps={{ autoComplete: "current-password" }}
            required
            disabled={isLoading}
          />
        )}
        <div className="form-footer-spacer" aria-hidden="true" />
        <div className="form-footer fixed-bottom">
          {isPasswordStep && (
            <Button
              className="w-100"
              type="button"
              variant="link"
              onClick={forgotPassword}
              disabled={isLoading}
            >
              Esqueci minha senha
            </Button>
          )}
          <Button
            type="submit"
            className="w-100 mt-3"
            variant="shop"
            disabled={isDisabled}
          >
            {isPasswordStep ? "Entrar" : "Continuar"}
          </Button>
        </div>
      </Form>
    </>
  );
}
